// Roast-level improvement suggestions.
//
// Consumes `GET /api/get_improvement_suggestions/{roast_id}` which returns an
// {text, data} envelope with two lists kept apart on purpose (issue #22):
//
//   * `improvements` — advice. The gap between this roast and the band
//     defined by roasts the operator rated highly. Every one cites the
//     rated roasts it came from and says how many there were.
//   * `drift` — an observation. How this batch differs from the operator's
//     typical one. Being unlike your average roast is not a fault, so this
//     section is labelled descriptive and never phrased as advice.
//
// When `improvements` is empty because the evidence bar was not met, the
// server says why (`reason`) and how many more ratings would fix it
// (`ratings_needed`). We show that rather than a blank list.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi } from "../useApi";
import type {
  ImprovementSuggestionsData,
  OperationalChange,
  ReferenceRoastInfo,
  RoastDetail as RoastDetailT,
  Suggestion,
} from "../types";
import { ErrorBox } from "../components/ErrorBox";
import { StarRating } from "../components/StarRating";
import { formatDate, formatNumber, formatTime } from "../format";

interface PeerFilters {
  only_rated: boolean;
  min_stars: number;
  use_reference: boolean;
}

export function RoastSuggestions() {
  const { roastId = "" } = useParams();
  const api = useApi();
  const [roast, setRoast] = useState<RoastDetailT | null>(null);
  const [data, setData] = useState<ImprovementSuggestionsData | null>(null);
  const [text, setText] = useState<string>("");
  const [err, setErr] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  // Server defaults: all peers, band from roasts rated ≥4★,
  // reference-aware.
  const [filters, setFilters] = useState<PeerFilters>({
    only_rated: false,
    min_stars: 4,
    use_reference: true,
  });

  // Fetch roast metadata once — title, stars, date live here.
  useEffect(() => {
    setRoast(null);
    api.getRoast(roastId).then(setRoast).catch(setErr);
  }, [api, roastId]);

  // Suggestions reload on every filter change.
  useEffect(() => {
    setLoading(true);
    setErr(null);
    api
      .getImprovementSuggestions(roastId, {
        only_rated: filters.only_rated,
        min_stars: filters.min_stars,
        use_reference: filters.use_reference,
      })
      .then((envl) => {
        setData(envl.data);
        setText(envl.text ?? "");
      })
      .catch(setErr)
      .finally(() => setLoading(false));
  }, [api, roastId, filters.only_rated, filters.min_stars, filters.use_reference]);

  if (err && !data) return <ErrorBox error={err} />;

  const unit = roast?.temp_unit ?? "C";

  return (
    <div className="col">
      <div>
        <Link to={`/roasts/${encodeURIComponent(roastId)}`}>← Back to roast</Link>
      </div>

      <div className="card">
        <div className="row">
          <div>
            <h1 style={{ margin: "0 0 4px 0" }}>What to change next time</h1>
            <div className="muted small">
              {roast ? (
                <>
                  {roast.coffee_label ?? roast.coffee_id ?? roast.name}
                  {" · "}
                  {formatDate(roast.roast_date)}
                </>
              ) : (
                "Loading…"
              )}
            </div>
          </div>
          {roast && roast.rating.stars > 0 && (
            <div className="right">
              <StarRating value={roast.rating.stars} size={20} />
            </div>
          )}
        </div>
      </div>

      <PeerPoolControls
        filters={filters}
        setFilters={setFilters}
        data={data}
        loading={loading}
      />

      <ErrorBox error={err} />

      {text && <p className="muted small">{text}</p>}

      {loading && !data ? (
        <SkeletonList count={3} />
      ) : data == null ? (
        <div className="card muted">No suggestions available for this roast yet.</div>
      ) : (
        <>
          {/* ---- Improvements: advice, backed by ratings ------------ */}
          <section className="col">
            <div className="row" style={{ alignItems: "baseline" }}>
              <h2 style={{ margin: 0 }}>Improvements</h2>
              <span className="badge ok">advice · backed by your ratings</span>
              <span className="muted small">
                band from {data.rated_count} roast{data.rated_count === 1 ? "" : "s"} rated ≥{data.min_stars}★
              </span>
            </div>
            {data.improvements.length === 0 ? (
              <ImprovementsEmpty data={data} />
            ) : (
              data.improvements.map((s) => (
                <SuggestionCard key={`imp-${s.parameter}`} s={s} unit={unit} />
              ))
            )}
          </section>

          {/* ---- Knob-level changes vs the hero roast --------------- */}
          {(data.operational_changes.length > 0 || data.recipe_alignment.withheld) && (
            <section className="col">
              <div className="row" style={{ alignItems: "baseline" }}>
                <h2 style={{ margin: 0 }}>Knob changes vs the hero roast</h2>
                {data.recipe_alignment.phases && (
                  <span className="muted small">
                    phases from {data.recipe_alignment.phases.source}
                    {" · "}yellowing {data.recipe_alignment.phases.yellowing_c}°C
                    {" · "}FC {data.recipe_alignment.phases.first_crack_c}°C IBTS
                  </span>
                )}
              </div>
              {data.recipe_alignment.withheld && (
                <div className="section-note small">
                  <strong>Per-step changes withheld.</strong>{" "}
                  {data.recipe_alignment.withheld}
                  {data.recipe_alignment.quality != null && (
                    <span className="muted">
                      {" "}(alignment quality {data.recipe_alignment.quality.toFixed(2)})
                    </span>
                  )}
                </div>
              )}
              {data.operational_changes.length > 0 && (
                <div className="card">
                  <ul className="plain">
                    {data.operational_changes.map((o, i) => (
                      <OperationalRow key={`${o.phase}-${o.control}-${i}`} o={o} />
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* ---- Drift: descriptive only ----------------------------- */}
          <section className="col">
            <div className="row" style={{ alignItems: "baseline" }}>
              <h2 style={{ margin: 0 }}>Drift</h2>
              <span className="badge">descriptive · not advice</span>
            </div>
            <div className="section-note small muted">
              How this batch differs from your typical one across{" "}
              {data.peer_count} peer roast{data.peer_count === 1 ? "" : "s"}.
              Being unlike your average roast is not a fault — nothing here
              is backed by a rating, so nothing here is a recommendation.
            </div>
            {data.drift.length === 0 ? (
              <p className="muted small" style={{ margin: 0 }}>
                {data.peer_count === 0
                  ? "No peer roasts of this coffee to compare against."
                  : "Nothing about this batch is materially unlike your typical one."}
              </p>
            ) : (
              data.drift.map((s) => (
                <SuggestionCard key={`drift-${s.parameter}`} s={s} unit={unit} />
              ))
            )}
          </section>
        </>
      )}
    </div>
  );
}

// Peer-pool configuration + reference-roast banner. The three knobs match
// the server's query parameters one-to-one; there's no hidden transformation.
function PeerPoolControls({
  filters,
  setFilters,
  data,
  loading,
}: {
  filters: PeerFilters;
  setFilters: (f: PeerFilters) => void;
  data: ImprovementSuggestionsData | null;
  loading: boolean;
}) {
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}>
        <strong>Peer pool</strong>
        {data && (
          <span className="muted small">
            {data.peer_count} peer roast{data.peer_count === 1 ? "" : "s"}
            {data.only_rated ? " · rated only" : " · all roasts"}
            {` · band from ≥${data.min_stars}★`}
          </span>
        )}
        {loading && <span className="muted small">Refreshing…</span>}
      </div>
      <div className="filter-bar">
        <label style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={filters.only_rated}
            onChange={(e) =>
              setFilters({ ...filters, only_rated: e.target.checked })
            }
          />
          <span>Rated peers only</span>
        </label>
        <label>
          Band from roasts rated
          <select
            value={filters.min_stars}
            onChange={(e) =>
              setFilters({ ...filters, min_stars: Number(e.target.value) })
            }
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                ≥ {n}★
              </option>
            ))}
          </select>
        </label>
        <label style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={filters.use_reference}
            onChange={(e) =>
              setFilters({ ...filters, use_reference: e.target.checked })
            }
          />
          <span>Compare knobs to hero roast</span>
        </label>
      </div>

      {data?.reference && <ReferenceBanner reference={data.reference} />}
    </div>
  );
}

// Called out above the suggestion list — the "hero roast" the knob-level
// changes are compared against.
function ReferenceBanner({ reference }: { reference: ReferenceRoastInfo }) {
  return (
    <div
      className="row small"
      style={{
        marginTop: 8,
        padding: "8px 10px",
        background: "var(--bg-alt)",
        borderRadius: "var(--radius)",
      }}
    >
      <span className="muted">Hero roast:</span>
      <Link to={`/roasts/${encodeURIComponent(reference.roast_id)}`} className="mono">
        {reference.roast_id}
      </Link>
      <StarRating value={reference.stars} size={12} />
      <span className="muted">{formatDate(reference.roast_date)}</span>
      <span className="badge">{reference.source}</span>
      {reference.rationale && <span className="muted">· {reference.rationale}</span>}
    </div>
  );
}

function SuggestionCard({ s, unit }: { s: Suggestion; unit: "C" | "F" }) {
  const pretty = prettyParameter(s.parameter);
  const isDrift = s.kind === "drift";
  const delta = formatDelta(s);
  const ev = s.evidence;
  return (
    <div className="card">
      <div className="row" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <strong style={{ fontSize: "1.05em" }}>{pretty.label}</strong>
            <span className={`badge ${isDrift ? "" : "ok"}`}>
              {isDrift ? `differs: ${s.direction}` : s.direction}
            </span>
            <span className="badge">{prettyTargetSource(s.target_source)}</span>
            {s.temp_source && (
              <span className="badge" title="Which probe these temperatures are quoted in">
                {s.temp_source.toUpperCase()} probe
              </span>
            )}
          </div>
          <div className="muted small" style={{ marginTop: 4 }}>
            {pretty.hint}
          </div>
        </div>
        <div className="right" style={{ textAlign: "right" }}>
          <div className="mono small muted">
            {formatParamValue(s.parameter, s.current, unit)} →{" "}
            <strong style={{ color: "var(--fg)" }}>
              {formatParamValue(s.parameter, s.target, unit)}
            </strong>
          </div>
          {delta && <div className="mono small">{delta}</div>}
        </div>
      </div>

      {s.rationale && <p style={{ margin: "10px 0 0 0" }}>{s.rationale}</p>}

      {!isDrift && (
        <div className="row small" style={{ gap: 6, marginTop: 10, flexWrap: "wrap" }}>
          <span className="muted">Evidence:</span>
          {ev.n_rated != null ? (
            <>
              <span className="badge ok">
                {ev.n_rated} rated roast{ev.n_rated === 1 ? "" : "s"}
              </span>
              {ev.stars && ev.stars.length > 0 && (
                <span className="badge">{ev.stars.map((n) => `${n}★`).join(" / ")}</span>
              )}
              {ev.band && (
                <span className="badge mono">
                  band {formatParamValue(s.parameter, ev.band.low, unit)}–
                  {formatParamValue(s.parameter, ev.band.high, unit)} ({ev.band.method})
                </span>
              )}
            </>
          ) : (
            <span className="muted">none recorded</span>
          )}
        </div>
      )}

      {s.caveats.length > 0 && (
        <ul className="small muted" style={{ margin: "8px 0 0" }}>
          {s.caveats.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      )}

      {s.cited_roast_ids.length > 0 && (
        <div className="row small" style={{ gap: 6, marginTop: 10, flexWrap: "wrap" }}>
          <span className="muted">{isDrift ? "Compared with:" : "Citing:"}</span>
          {s.cited_roast_ids.map((id) => (
            <Link
              key={id}
              to={`/roasts/${encodeURIComponent(id)}`}
              className="badge"
              style={{ textDecoration: "none" }}
            >
              {id}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function OperationalRow({ o }: { o: OperationalChange }) {
  return (
    <li className="row small" style={{ padding: "4px 0" }}>
      <span className="badge">{o.phase}</span>
      <span>{o.description}</span>
      <span className="mono muted">
        {o.control} {o.current ?? "–"} → {o.target ?? "–"}
        {o.cited_threshold_c != null && ` @ ${o.cited_threshold_c.toFixed(0)}°C IBTS`}
      </span>
      <Link to={`/roasts/${encodeURIComponent(o.cited_roast_id)}`} className="mono right">
        {o.cited_roast_id}
      </Link>
    </li>
  );
}

// Empty improvements is a refusal with a reason, not a blank list
// (issue #22): the server says what would change the answer.
function ImprovementsEmpty({ data }: { data: ImprovementSuggestionsData }) {
  if (data.reason) {
    return (
      <div className="card">
        <p style={{ marginTop: 0 }}>
          <span className="badge warn">not enough rated roasts</span>
        </p>
        <p className="small" style={{ margin: 0 }}>
          {data.reason}
        </p>
        {data.ratings_needed > 0 && (
          <p className="small muted" style={{ margin: "6px 0 0" }}>
            Rate {data.ratings_needed} more roast{data.ratings_needed === 1 ? "" : "s"} of this
            coffee ≥{data.min_stars}★ and the band can be built.{" "}
            <Link to="/?only_unrated=1">Unrated roasts →</Link>
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="card">
      <p className="muted" style={{ margin: 0 }}>
        Every measured parameter sits inside the band your {data.rated_count} rated
        roast{data.rated_count === 1 ? "" : "s"} define — nothing to change on
        this evidence.
      </p>
    </div>
  );
}

function SkeletonList({ count }: { count: number }) {
  return (
    <div className="col">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card skeleton-card">
          <div className="skeleton skeleton-line" style={{ width: "40%" }} />
          <div
            className="skeleton skeleton-line"
            style={{ width: "75%", marginTop: 10 }}
          />
          <div
            className="skeleton skeleton-line"
            style={{ width: "55%", marginTop: 6 }}
          />
        </div>
      ))}
    </div>
  );
}

// --- Parameter formatting helpers ---------------------------------------
//
// Parameter names come from the server as internal keys like
// `development_pct` or `fc_time_s`. Map them to display labels plus a
// tight hint so the operator doesn't have to decode the metric every time.

function prettyParameter(p: string): { label: string; hint: string } {
  const table: Record<string, { label: string; hint: string }> = {
    max_ror: {
      label: "Max RoR",
      hint: "Peak rate of rise — higher means a hotter early ramp.",
    },
    ror_at_first_crack: {
      label: "RoR at first crack",
      hint: "Momentum entering development.",
    },
    end_ror: {
      label: "End RoR",
      hint: "RoR near drop.",
    },
    development_pct: {
      label: "Development %",
      hint: "Share of total roast after first crack.",
    },
    development_duration_s: {
      label: "Development time",
      hint: "First crack to drop.",
    },
    fc_time_s: {
      label: "First crack time",
      hint: "Elapsed time to first crack.",
    },
    total_roast_time_s: {
      label: "Total roast time",
      hint: "Charge to drop.",
    },
    drop_temp: {
      label: "Drop temperature",
      hint: "Temperature at drop — see the probe tag.",
    },
    drop_ibts_c: {
      label: "Drop temperature (IBTS)",
      hint: "IBTS reading at drop.",
    },
    charge_temp: {
      label: "Charge temperature",
      hint: "Drum temperature at charge.",
    },
    weight_loss_pct: {
      label: "Weight loss",
      hint: "Green to roasted, percent.",
    },
  };
  if (table[p]) return table[p];
  const label = p.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  return { label, hint: "" };
}

// Value formatter aware of parameter units. Falls back to plain number.
function formatParamValue(param: string, value: number | null, unit: "C" | "F"): string {
  if (value == null) return "–";
  if (param.endsWith("_s") || (param.includes("time") && !param.includes("pct"))) {
    return formatTime(value);
  }
  if (param.includes("pct") || param.includes("ratio")) {
    return `${value.toFixed(1)}%`;
  }
  if (param.includes("temp") || param.endsWith("_c")) {
    return `${value.toFixed(1)}°${unit}`;
  }
  if (param.includes("ror")) {
    return `${value.toFixed(1)}/min`;
  }
  return formatNumber(value, 2);
}

// Delta next to the arrow, with sign. `delta` is current − target.
function formatDelta(s: Suggestion): string {
  if (s.delta == null) return "";
  const sign = s.delta >= 0 ? "+" : "−";
  const abs = Math.abs(s.delta);
  if (s.parameter.endsWith("_s") || (s.parameter.includes("time") && !s.parameter.includes("pct"))) {
    return `${sign}${formatTime(abs)}`;
  }
  if (s.parameter.includes("pct") || s.parameter.includes("ratio")) {
    return `${sign}${abs.toFixed(1)}%`;
  }
  return `${sign}${abs.toFixed(2)}`;
}

function prettyTargetSource(s: string): string {
  switch (s) {
    case "rated_band":
      return "target: your rated band";
    case "peer_median_weighted":
      return "reference: peer median";
    case "reference_roast":
      return "reference: hero roast";
    default:
      return s.replace(/_/g, " ");
  }
}
